import { BarChart3, FileText, Handshake, History, Receipt, Truck } from "lucide-react";
import { type SyntheticEvent, useState } from "react";
import { SelectField, TextField, ToggleField } from "../../web/components/fields";
import { RecordWindow } from "../../web/components/RecordWindow";
import { CheckedPill } from "./CheckedMark";
import { archiveAction } from "../../web/components/recordActions";
import { Badge, Box, ErrorNotice, Issues, Notice } from "../../web/components/ui";
import { timeAgo } from "../../web/lib/format";
import { useCompany } from "../../web/lib/company";
import { useMoney } from "../../web/lib/money";
import { useFreshRecord } from "../../web/lib/queries";
import { useNewUuid } from "../../web/lib/newUuid";
import { can, itemName, priceQuote, usePriceListSaver, whyNot } from "./data";
import { ORDER_METHODS, PAYMENT_METHODS, type PriceListData, type SupplierRow } from "./meta";
import { AGE } from "./SupplierPricesTab";
import { quoteKey, quoteLabel, quoteOptions } from "./QuotedAmount";
import { ageOf, hasAmount, quotedPrice, usualQuote } from "./supplierPricing";
import { SupplierStatsTab, withStatsTab } from "./StatsTab";
import { ChangedNotice, HistoryTab } from "./HistoryTab";
import { pricesDueBySupplier } from "./attention";

/*
 * One supplier, in a window like an item's: Details (ServiceM8's fields), Ordering (our account
 * with them, how they take orders and payment) and Prices (how they quote, and everything we buy
 * from them). Fields save as you leave them, where the supplier lives (ServiceM8 or this app).
 */

const TABS = [
    { id: "details", label: "Details", icon: <FileText className="size-5" /> },
    { id: "ordering", label: "Ordering", icon: <Handshake className="size-5" /> },
    { id: "prices", label: "Prices", icon: <Receipt className="size-5" /> },
    { id: "stats", label: "Stats", icon: <BarChart3 className="size-5" /> },
    { id: "history", label: "History", icon: <History className="size-5" /> },
];


const emailCheck = (v: string) => (v === "" || /^[^\s@]+@[^\s@]+$/.test(v) ? null : "Enter a valid email address");

export function SupplierWindow(
    props: Readonly<{ data: PriceListData; uuid: string; tab: string; onTab: (tab: string) => void; onClose: () => void; onOpenItem: (uuid: string) => void }>,
) {
    const company = useCompany();
    const saver = usePriceListSaver();
    const s = props.data.suppliers.find((x) => x.uuid === props.uuid);
    useFreshRecord(company.id, "supplier", s ? props.uuid : null);
    const [error, setError] = useState<Error | null>(null);

    if (!s) {
        return (
            <RecordWindow kind="supplier" tone="teal" icon={<Truck className="size-4" />} title="Supplier not found" tabs={[]} tab="" onTab={() => undefined} onClose={props.onClose}>
                <Notice>This supplier doesn't exist any more.</Notice>
            </RecordWindow>
        );
    }
    const canEdit = can(props.data, "suppliers.edit");
    // Their prices are what the business pays: only with See costs (the server leaves them out otherwise).
    const tabs = withStatsTab(TABS, props.data).filter((t) => t.id !== "prices" || can(props.data, "costs.view"));
    const tab = tabs.some((t) => t.id === props.tab) ? props.tab : "details";
    const setActive = (active: boolean) => {
        setError(null);
        saver.updateSupplier(s.uuid, { active }).catch((err: Error) => setError(err));
    };

    return (
        <RecordWindow kind="supplier" tone="teal" icon={<Truck className="size-4" />} share
            title={<span className="truncate">{s.name || "Untitled supplier"}</span>}
            label={s.name || "Untitled supplier"}
            subtitle={s.account_number ? `Account ${s.account_number}` : undefined}
            barExtra={s.active ? <CheckedPill data={props.data} kind="supplier" id={s.uuid} onError={setError} /> : null}
            tabs={tabs}
            tab={tab}
            onTab={props.onTab}
            onClose={props.onClose}
            actions={
                canEdit
                    ? [
                          archiveAction("supplier", s.name || "Untitled supplier", s.active, setActive),
                      ]
                    : undefined
            }
            notices={
                <>
                    <ErrorNotice error={error} />
                    {/* Its problems as one box. */}
                    <Issues>
                        <ChangedNotice object="supplier" uuid={s.uuid} data={props.data} />
                        <RecheckNotice data={props.data} supplier={s} onPrices={tab === "prices" ? undefined : () => props.onTab("prices")} />
                    </Issues>
                    {s.active ? null : <Notice tone="warning">This supplier is archived.</Notice>}
                    {props.data.suppliersUnavailable ? <Notice>{props.data.suppliersUnavailable}</Notice> : null}
                    {whyNot(props.data, "suppliers.edit") ? <Notice>{whyNot(props.data, "suppliers.edit")}</Notice> : null}
                </>
            }
        >
            <div>
                {tab === "details" && <DetailsTab supplier={s} canEdit={canEdit} />}
                {tab === "ordering" && <OrderingTab supplier={s} canEdit={canEdit} />}
                {tab === "prices" && <PricesTab data={props.data} supplier={s} onOpenItem={props.onOpenItem} />}
                {tab === "stats" && <SupplierStatsTab uuid={s.uuid} />}
                {tab === "history" && <HistoryTab object="supplier" uuid={s.uuid} data={props.data} />}
            </div>
        </RecordWindow>
    );
}

/** Their prices (for active items) due for a recheck, and the way to them. */
function RecheckNotice({ data, supplier, onPrices }: Readonly<{ data: PriceListData; supplier: SupplierRow; onPrices?: () => void }>) {
    const active = new Set(data.materials.filter((m) => m.active).map((m) => m.uuid));
    const due = pricesDueBySupplier(data.supplierPrices, active, data.settings).get(supplier.uuid)?.due ?? 0;
    if (!supplier.active || !due) return null;
    return (
        <Notice
            tone="warning"
            title="Prices to recheck."
            actions={
                onPrices ? (
                    <button type="button" className="btn-secondary" onClick={onPrices}>
                        See their prices
                    </button>
                ) : null
            }
        >
            {due.toLocaleString()} of their prices {due === 1 ? "is" : "are"} due for a recheck.
        </Notice>
    );
}

function DetailsTab({ supplier: s, canEdit }: Readonly<{ supplier: SupplierRow; canEdit: boolean }>) {
    const saver = usePriceListSaver();
    const save = (patch: Parameters<typeof saver.updateSupplier>[1]) => saver.updateSupplier(s.uuid, patch);
    return (
        <>
            <Box>
                <TextField label="Name" value={s.name} disabled={!canEdit} validate={(v) => (v ? null : "Name is required")} onSave={(name) => save({ name })} />
            </Box>
            <Box>
                <div className="grid gap-4 sm:grid-cols-2">
                    <TextField label="Phone" value={s.phone} disabled={!canEdit} onSave={(phone) => save({ phone })} />
                    <TextField label="Email" value={s.email} disabled={!canEdit} validate={emailCheck} onSave={(email) => save({ email })} />
                </div>
                <TextField label="Address" multiline value={s.address} disabled={!canEdit} onSave={(address) => save({ address })} />
            </Box>
            <Box className="grid gap-4 space-y-0 sm:grid-cols-2">
                <TextField label="Your account number" value={s.account_number} disabled={!canEdit} onSave={(account_number) => save({ account_number })} />
                <TextField label="Business number" value={s.business_number} disabled={!canEdit} hint="ABN, EIN, VAT number…" onSave={(business_number) => save({ business_number })} />
            </Box>
        </>
    );
}

function OrderingTab({ supplier: s, canEdit }: Readonly<{ supplier: SupplierRow; canEdit: boolean }>) {
    const saver = usePriceListSaver();
    const d = s.details;
    const save = (patch: Parameters<typeof saver.updateSupplierDetails>[1]) => saver.updateSupplierDetails(s.uuid, patch);
    const togglePayment = (id: string, on: boolean) => save({ paymentMethods: on ? [...d.paymentMethods, id] : d.paymentMethods.filter((m) => m !== id) });
    const toggleOrder = (id: string, on: boolean) =>
        save({ orderMethods: on ? [...d.orderMethods, { method: id, detail: "" }] : d.orderMethods.filter((m) => m.method !== id) });
    const setOrderDetail = (id: string, detail: string) => save({ orderMethods: d.orderMethods.map((m) => (m.method === id ? { ...m, detail } : m)) });

    return (
        <>
            <Box>
                <ToggleField label="We have an account with them" checked={d.hasAccount} disabled={!canEdit} onSave={(hasAccount) => save({ hasAccount })} />
                {d.hasAccount ? (
                    <TextField label="Payment terms" value={d.accountTerms} placeholder="e.g. 30 days EOM" disabled={!canEdit} onSave={(accountTerms) => save({ accountTerms })} className="max-w-sm" />
                ) : null}
            </Box>

            <Box title="Their rep">
                <div className="grid gap-4 sm:grid-cols-3">
                    <TextField label="Name" value={d.repName} disabled={!canEdit} onSave={(repName) => save({ repName })} />
                    <TextField label="Phone" value={d.repPhone} disabled={!canEdit} onSave={(repPhone) => save({ repPhone })} />
                    <TextField label="Email" value={d.repEmail} disabled={!canEdit} validate={emailCheck} onSave={(repEmail) => save({ repEmail })} />
                </div>
            </Box>

            <Box title="How they take payment">
                {/* Rows as tall as a field, like How to order below, so the two line up. */}
                <div className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
                    {PAYMENT_METHODS.map((m) => (
                        <div key={m.id} className="flex min-h-8 items-center">
                            <ToggleField label={m.label} checked={d.paymentMethods.includes(m.id)} disabled={!canEdit} onSave={(on) => togglePayment(m.id, on)} />
                        </div>
                    ))}
                </div>
                {d.paymentMethods.length > 1 ? (
                    <SelectField
                        label="We prefer"
                        value={d.preferredPayment ?? ""}
                        options={[{ value: "", label: "No preference" }, ...PAYMENT_METHODS.filter((m) => d.paymentMethods.includes(m.id)).map((m) => ({ value: m.id, label: m.label }))]}
                        disabled={!canEdit}
                        onSave={(v) => save({ preferredPayment: v === "" ? null : v })}
                        className="max-w-xs"
                    />
                ) : null}
            </Box>

            <Box title="How to order">
                {/*
                  * Each way to order, with its detail (the website, the email...) beside it once it's on. Every
                  * row is as tall as a field, so turning one on shows its field without moving anything.
                  */}
                <div className="space-y-1">
                    {ORDER_METHODS.map((m) => {
                        const chosen = d.orderMethods.find((o) => o.method === m.id);
                        return (
                            <div key={m.id} className="grid min-h-8 items-center gap-x-4 gap-y-1 sm:grid-cols-2">
                                <ToggleField label={m.label} checked={Boolean(chosen)} disabled={!canEdit} onSave={(on) => toggleOrder(m.id, on)} />
                                {chosen && m.detail ? (
                                    <TextField
                                        label={m.detail}
                                        hideLabel
                                        value={chosen.detail}
                                        placeholder={m.placeholder}
                                        disabled={!canEdit}
                                        onSave={(v) => setOrderDetail(m.id, v)}
                                    />
                                ) : null}
                            </div>
                        );
                    })}
                </div>
                {d.orderMethods.length > 1 ? (
                    <SelectField
                        label="We prefer"
                        value={d.preferredOrder ?? ""}
                        options={[{ value: "", label: "No preference" }, ...ORDER_METHODS.filter((m) => d.orderMethods.some((o) => o.method === m.id)).map((m) => ({ value: m.id, label: m.label }))]}
                        disabled={!canEdit}
                        onSave={(v) => save({ preferredOrder: v === "" ? null : v })}
                        className="max-w-xs"
                    />
                ) : null}
            </Box>

            <Box>
                <TextField label="Notes" multiline value={d.notes} placeholder="e.g. free delivery over $200" disabled={!canEdit} onSave={(notes) => save({ notes })} />
            </Box>
        </>
    );
}

/** How they quote (with tax or not, at which rate), and everything we have a price for from them. */
function PricesTab({ data, supplier: s, onOpenItem }: Readonly<{ data: PriceListData; supplier: SupplierRow; onOpenItem: (uuid: string) => void }>) {
    const fmt = useMoney();
    const saver = usePriceListSaver();
    const options = quoteOptions(data.taxRates);
    const items = new Map(data.materials.map((m) => [m.uuid, m]));
    const nameOf = (uuid: string) => {
        const m = items.get(uuid);
        return m ? itemName(data, m) : "Unknown item";
    };
    const prices = data.supplierPrices.filter((p) => p.supplierUuid === s.uuid).sort((a, b) => nameOf(a.materialUuid).localeCompare(nameOf(b.materialUuid)));
    const canEdit = can(data, "suppliers.edit");

    return (
        <>
            {options.length ? (
                <Box>
                    <SelectField
                        label="Their prices are usually quoted"
                        value={quoteKey(usualQuote(s.details), data.taxRates)}
                        disabled={!canEdit}
                        options={options}
                        hint="How each new price from them starts: typed the way they quote it. A price quoted another way (e.g. a tax-free item) is changed on that price."
                        onSave={(key) => {
                            const quote = options.find((o) => o.value === key)?.quote;
                            return quote ? saver.updateSupplierDetails(s.uuid, { purchaseTaxRateUuid: quote.rateUuid, pricesIncludeTax: quote.includesTax }) : Promise.resolve();
                        }}
                        className="sm:max-w-[calc(50%-0.5rem)]"
                    />
                </Box>
            ) : null}
            {/* With prices, the list fills the section top to bottom (as on an item's Suppliers tab). */}
            <Box className={prices.length ? "py-0" : undefined}>
                {prices.length === 0 ? (
                    <p className="text-muted">No prices from them yet. Add them on each item&apos;s Suppliers tab.</p>
                ) : (
                    <table className="list is-fixed -mx-4 w-[calc(100%+2rem)]">
                        <colgroup>
                            <col />
                            <col className="w-32" />
                            <col className="w-32" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th>Item</th>
                                <th className="text-right">Price each</th>
                                <th className="text-right">Checked</th>
                            </tr>
                        </thead>
                        <tbody>
                            {prices.map((p) => {
                                const age = AGE[ageOf(p.checkedAt, data.settings)];
                                const { quote, tax } = priceQuote(data, p, s);
                                return (
                                    <tr key={p.id}>
                                        <td>
                                            <span className="flex min-w-0 items-center gap-2">
                                                <button type="button" className="link-button truncate text-left font-medium" onClick={() => onOpenItem(p.materialUuid)}>
                                                    {nameOf(p.materialUuid)}
                                                </button>
                                                {p.minQty > 1 ? <Badge tone="blue" title={`This price is for orders of ${fmt.number(p.minQty)} or more`}>From {fmt.number(p.minQty)}</Badge> : null}
                                            </span>
                                        </td>
                                        <td className="text-right whitespace-nowrap">
                                            {hasAmount(p) ? (
                                                <>
                                                    {fmt.money(quotedPrice(p.priceNet, quote, tax))} <span className="text-xs text-muted">{quoteLabel(quote, data.taxRates)}</span>
                                                </>
                                            ) : (
                                                <span className="text-muted">No price</span>
                                            )}
                                        </td>
                                        <td className="text-right">
                                            <Badge tone={age.tone} title={`Checked ${timeAgo(p.checkedAt)}`}>
                                                {age.label}
                                            </Badge>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </Box>
        </>
    );
}

/** Adding a supplier: a name, then it opens in its own window to fill in the rest. */
export function NewSupplierWindow(props: Readonly<{ data: PriceListData; onCreated: (uuid: string) => void; onClose: () => void }>) {
    const saver = usePriceListSaver();
    // Its uuid, the same for every try (a retry after a timeout can't make a second).
    const newUuid = useNewUuid();
    const [name, setName] = useState("");
    const [error, setError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);
    const submit = (e: SyntheticEvent) => {
        e.preventDefault();
        if (!name.trim()) return;
        setBusy(true);
        setError(null);
        saver
            .createSupplier({ name: name.trim(), uuid: newUuid.get() })
            .then((s) => {
                newUuid.made();
                props.onCreated(s.uuid);
            })
            .catch((err: Error) => setError(err))
            .finally(() => setBusy(false));
    };
    return (
        <RecordWindow kind="supplier" tone="teal" icon={<Truck className="size-4" />} title="New supplier" tabs={[{ id: "details", label: "Details", icon: <FileText className="size-5" /> }]} tab="details" onTab={() => undefined} onClose={props.onClose}>
            <form className="mx-auto w-full max-w-lg space-y-4" onSubmit={submit}>
                <label className="block space-y-1">
                    <span className="text-xs font-medium text-muted">Name</span>
                    <input className="input text-sm" value={name} onChange={(e) => setName(e.target.value)} required />
                </label>
                {props.data.suppliersUnavailable ? <Notice>{props.data.suppliersUnavailable}</Notice> : null}
                <ErrorNotice error={error} />
                <div className="flex justify-end gap-2">
                    <button type="button" className="btn-secondary" onClick={props.onClose}>
                        Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={busy || !name.trim() || props.data.suppliersUnavailable !== null}>
                        Add supplier
                    </button>
                </div>
            </form>
        </RecordWindow>
    );
}
